import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { customerRepo, taxDocumentRepo } from "../../services";
import { taxDocumentService, TAX_DOC_TYPES, buyerOf, sellerOf, sellerProblems, taxProviderReady } from "../../services/taxDocument";
import { useApp } from "../../context/AppContext";
import { useI18n } from "../../i18n";
import { useBranding } from "../../config/branding";
import TaxDocumentPaper, { PrintArea } from "../../components/TaxDocumentPaper";
import BuyerTaxFields from "../../components/BuyerTaxFields";
import { uid } from "../../mock/store";
import { calcVat } from "../../utils/thaiTax";
import {
  Button, Card, DataTable, Input, Modal, PageHead, Select, StatusBadge, Tabs, Textarea, baht, formatDate, useAsync, useTableState,
} from "../../components/ui";

const TABS = {
  all: () => true,
  draft: (row) => row.status === "draft",
  issued: (row) => ["issued", "pending_rd"].includes(row.status) && row.docType !== "credit_note",
  credit_note: (row) => row.docType === "credit_note",
  cancelled: (row) => row.status === "cancelled",
};

const newLine = () => ({ lineId: uid("ln"), name: "", price: 0, quantity: 1 });
const EMPTY_FORM = { docType: "tax_invoice", customerId: "", buyer: {}, lines: [newLine()], discount: 0, priceMode: "exclusive", note: "", reason: "" };

export default function TaxInvoices() {
  const { orgId, organization, toast, profile } = useApp();
  const { tx } = useI18n();
  const brand = useBranding();
  const tableState = useTableState();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState("all");
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState(null);
  const [editor, setEditor] = useState(null); // { id?: string } — null = ปิด
  const [form, setForm] = useState(EMPTY_FORM);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [creditTarget, setCreditTarget] = useState(null);
  const [credit, setCredit] = useState({ amount: "", reason: "", remaining: 0 });
  const [printToken, setPrintToken] = useState(0);
  const [printCopies, setPrintCopies] = useState(["original"]);

  const by = profile?.email || profile?.fullname || "";
  const seller = sellerOf(organization);
  const problems = sellerProblems(seller);
  const providerReady = taxProviderReady();

  const { data: customers } = useAsync(() => customerRepo.all(orgId), [orgId], []);
  const { data, loading, reload } = useAsync(
    () =>
      taxDocumentRepo.list(orgId, {
        page: tableState.page,
        perPage: tableState.perPage,
        search: tableState.search,
        sortBy: "createdAt",
        sortDir: "desc",
        filter: TABS[tab],
      }),
    [orgId, tab, tableState.page, tableState.perPage, tableState.search]
  );

  const fail = (err) => toast(err?.message || tx("ทำรายการไม่สำเร็จ", "Action failed"), "error");

  // เปิดฉบับร่างที่เพิ่งสร้างจากหน้าใบแจ้งหนี้/ใบเสร็จ (?open=<id>)
  const openId = params.get("open");
  useEffect(() => {
    if (!openId || !orgId) return;
    taxDocumentRepo.get(orgId, openId).then((row) => {
      if (row) (row.status === "draft" ? openEditor(row) : setView(row));
      setParams({}, { replace: true });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openId, orgId]);

  const run = async (fn, okMessage) => {
    if (busy) return;
    setBusy(true);
    try {
      const result = await fn();
      if (okMessage) toast(okMessage, "success");
      reload();
      return result;
    } catch (err) {
      fail(err);
      return null;
    } finally {
      setBusy(false);
    }
  };

  // ---------------- สร้าง/แก้ฉบับร่าง ----------------
  const openCreate = () => {
    setForm({ ...EMPTY_FORM, lines: [newLine()] });
    setEditor({});
  };

  function openEditor(row) {
    setForm({
      docType: row.docType,
      customerId: row.customerId || "",
      buyer: { ...row.buyer },
      lines: (row.items || []).map((item) => ({ lineId: uid("ln"), name: item.name, price: item.price, quantity: item.quantity })),
      discount: row.discount || 0,
      priceMode: row.priceMode || "exclusive",
      note: row.note || "",
      reason: row.reason || "",
    });
    setEditor({ id: row.id, docType: row.docType });
  }

  const pickCustomer = (customerId) => {
    const customer = (customers || []).find((c) => c.id === customerId);
    setForm((prev) => ({ ...prev, customerId, buyer: customer ? buyerOf(customer) : {} }));
  };

  const setBuyerTax = (next) => setForm((prev) => ({ ...prev, buyer: { ...prev.buyer, taxId: next.taxId, branchCode: next.branchCode, isJuristic: next.isJuristic } }));
  const setLine = (index, patch) => setForm((prev) => ({ ...prev, lines: prev.lines.map((line, i) => (i === index ? { ...line, ...patch } : line)) }));

  const calc = calcVat({ lines: form.lines, discount: form.discount, mode: form.priceMode });
  const isCreditEdit = editor?.docType === "credit_note";

  const saveEditor = async () => {
    const buyer = { ...form.buyer };
    if (!editor.id && !String(buyer.name || "").trim()) return toast(tx("กรอกชื่อผู้ซื้อ หรือเลือกลูกค้า", "Enter buyer name or pick a customer"), "error");
    const saved = await run(async () => {
      if (editor.id) {
        return taxDocumentService.updateDraft(orgId, editor.id, isCreditEdit ? { reason: form.reason, note: form.note } : { buyer, items: form.lines, discount: form.discount, priceMode: form.priceMode, note: form.note });
      }
      return taxDocumentService.createDraft({ orgId, organization, source: "manual", docType: form.docType, customer: (customers || []).find((c) => c.id === form.customerId), buyer, items: form.lines, discount: form.discount, priceMode: form.priceMode, note: form.note, by });
    }, tx("บันทึกฉบับร่างแล้ว", "Draft saved"));
    if (saved) setEditor(null);
  };

  // ---------------- ออก / ยกเลิก / ลดหนี้ ----------------
  const issue = async (row) => {
    if (!window.confirm(tx("ออกเอกสารนี้? ออกแล้วจะได้เลขที่เรียงและแก้ไขไม่ได้อีก", "Issue this document? It gets a sequential number and can no longer be edited."))) return;
    const issued = await run(() => taxDocumentService.issue({ orgId, organization, id: row.id, by }), tx("ออกเอกสารเรียบร้อย", "Document issued"));
    if (issued) {
      setEditor(null);
      setView(issued);
    }
  };

  const confirmCancel = async () => {
    if (!cancelReason.trim()) return toast(tx("กรุณาระบุเหตุผลที่ยกเลิก", "Please enter a reason"), "error");
    const done = await run(() => taxDocumentService.cancel({ orgId, id: cancelTarget.id, reason: cancelReason, by }), tx("ยกเลิกเอกสารแล้ว", "Document cancelled"));
    if (done) {
      setCancelTarget(null);
      setCancelReason("");
      setView(null);
    }
  };

  const openCredit = async (row) => {
    const credited = await taxDocumentService.creditedTotal(orgId, row.id);
    setCredit({ amount: "", reason: "", remaining: Math.round((row.total - credited) * 100) / 100 });
    setCreditTarget(row);
  };

  const confirmCredit = async () => {
    const draft = await run(
      () => taxDocumentService.createCreditNote({ orgId, organization, originalId: creditTarget.id, amount: Number(credit.amount), reason: credit.reason, by }),
      tx("สร้างใบลดหนี้ฉบับร่างแล้ว", "Credit note draft created")
    );
    if (draft) {
      setCreditTarget(null);
      setView(null);
      openEditor(draft);
    }
  };

  const submitRd = async (row) => {
    const result = await run(() => taxDocumentService.submitToRd({ orgId, id: row.id }));
    if (result && result.status !== "queued") toast(result.message, "error");
  };

  const print = (copies) => {
    setPrintCopies(copies);
    setPrintToken(Date.now());
  };

  const typeOptions = [
    { value: "tax_invoice", label: tx("ใบกำกับภาษี", "Tax invoice") },
    { value: "receipt_tax_invoice", label: tx("ใบเสร็จรับเงิน/ใบกำกับภาษี", "Receipt / Tax invoice") },
  ];

  const columns = [
    { key: "docNo", label: tx("เลขที่", "No."), render: (row) => row.docNo || <span className="u-muted">{tx("(ฉบับร่าง)", "(draft)")}</span> },
    { key: "docType", label: tx("ประเภท", "Type"), render: (row) => TAX_DOC_TYPES[row.docType]?.label || row.docType },
    { key: "buyer", label: tx("ผู้ซื้อ", "Buyer"), render: (row) => row.buyer?.name || "-" },
    { key: "ref", label: tx("อ้างอิง", "Ref"), render: (row) => row.refDocNo || row.invoiceNo || "-" },
    { key: "total", label: tx("ยอดรวม", "Total"), align: "right", render: (row) => baht(row.total) },
    { key: "issuedAt", label: tx("วันที่", "Date"), render: (row) => formatDate(row.issuedAt || row.createdAt) },
    { key: "status", label: tx("สถานะ", "Status"), render: (row) => <StatusBadge status={row.status} /> },
    {
      key: "action",
      label: "",
      render: (row) => (
        <div className="u-flex u-gap-4">
          <Button size="sm" onClick={() => setView(row)}>{tx("ดู", "View")}</Button>
          {row.status === "draft" ? <Button size="sm" variant="primary" onClick={() => openEditor(row)}>{tx("แก้ไข/ออก", "Edit / issue")}</Button> : null}
        </div>
      ),
    },
  ];

  const viewing = view;
  const canCancel = viewing && ["draft", "issued"].includes(viewing.status);
  const canCredit = viewing && viewing.docType !== "credit_note" && ["issued", "pending_rd"].includes(viewing.status);

  return (
    <>
      <PageHead
        title={tx("ใบกำกับภาษีอิเล็กทรอนิกส์ (e-Tax)", "e-Tax invoices")}
        subtitle={tx("ใบกำกับภาษี / ใบเสร็จรับเงิน-ใบกำกับภาษี / ใบลดหนี้ — ออกเลขที่เรียงอัตโนมัติ พิมพ์ได้", "Tax invoice / receipt / credit note with sequential numbers and print")}
        actions={<Button variant="primary" onClick={openCreate} disabled={!seller.vatRegistered}>+ {tx("สร้างเอกสารเอง", "New document")}</Button>}
      />

      {!providerReady ? (
        <Card className="u-mb-16" style={{ background: "#fffbeb", borderColor: "#fcd34d" }}>
          <div className="u-strong">{tx("ยังไม่ได้ส่งเอกสารเข้ากรมสรรพากร", "Documents are not sent to the Revenue Department yet")}</div>
          <div className="u-fs-13">
            {tx(
              "ตอนนี้ระบบออกเลขที่ เก็บ และพิมพ์เอกสารให้ได้ แต่ยังไม่ได้เชื่อมต่อผู้ให้บริการ e-Tax จึงยังไม่ส่งเข้ากรมสรรพากรจริง — ต้องต่อผู้ให้บริการที่ได้รับอนุญาตก่อนถึงจะใช้แทนใบกำกับภาษีอิเล็กทรอนิกส์ตามกฎหมาย",
              "The system issues, stores and prints documents but is not connected to an e-Tax provider yet, so nothing is sent to the Revenue Department."
            )}
          </div>
        </Card>
      ) : null}

      {problems.length ? (
        <Card className="u-mb-16" style={{ background: "#fef2f2", borderColor: "#fca5a5" }}>
          <div className="u-strong">{tx("ข้อมูลร้านยังไม่พร้อมออกใบกำกับภาษี", "Shop details are not ready")}</div>
          <ul className="u-fs-13" style={{ margin: "6px 0 8px 18px" }}>
            {problems.map((text) => <li key={text}>{text}</li>)}
          </ul>
          <Link className="btn-crm btn-primary-crm" to="/admin/organization">{tx("ไปแก้ที่ข้อมูลองค์กร", "Open organization settings")}</Link>
        </Card>
      ) : null}

      <Tabs
        tabs={[
          { key: "all", label: tx("ทั้งหมด", "All") },
          { key: "draft", label: tx("ฉบับร่าง", "Drafts") },
          { key: "issued", label: tx("ออกแล้ว", "Issued") },
          { key: "credit_note", label: tx("ใบลดหนี้", "Credit notes") },
          { key: "cancelled", label: tx("ยกเลิก", "Cancelled") },
        ]}
        value={tab}
        onChange={(next) => {
          setTab(next);
          tableState.setPage(1);
        }}
      />
      <div className="filter-bar">
        <input className="input-crm" style={{ maxWidth: 280 }} placeholder={tx("ค้นหาเลขที่ / ผู้ซื้อ / ใบแจ้งหนี้", "Search")} value={tableState.search} onChange={(e) => tableState.setSearch(e.target.value)} />
      </div>
      <DataTable
        loading={loading}
        rows={data?.rows || []}
        total={data?.total || 0}
        page={tableState.page}
        perPage={tableState.perPage}
        onPage={tableState.setPage}
        onPerPage={tableState.setPerPage}
        emptyText={tx("ยังไม่มีเอกสารภาษี — กด 'ออกใบกำกับภาษี' ที่ใบแจ้งหนี้/ใบเสร็จในหน้าเอกสารการเงิน หรือสร้างเอง", "No tax documents yet")}
        columns={columns}
      />

      {/* ดู / พิมพ์ */}
      <Modal
        open={!!viewing}
        size="lg"
        title={viewing ? `${TAX_DOC_TYPES[viewing.docType]?.label || ""} ${viewing.docNo || ""}` : ""}
        onClose={() => setView(null)}
        footer={
          viewing ? (
            <>
              {canCancel ? <Button variant="danger" onClick={() => { setCancelReason(""); setCancelTarget(viewing); }}>{tx("ยกเลิก", "Cancel doc")}</Button> : null}
              {canCredit ? <Button onClick={() => openCredit(viewing)}>{tx("ออกใบลดหนี้", "Credit note")}</Button> : null}
              {providerReady && viewing.status === "issued" ? <Button onClick={() => submitRd(viewing)} disabled={busy}>{tx("ส่งกรมสรรพากร", "Send to RD")}</Button> : null}
              <Button onClick={() => print(["original", "copy"])}>{tx("พิมพ์ต้นฉบับ+สำเนา", "Print both")}</Button>
              <Button onClick={() => print(["copy"])}>{tx("พิมพ์สำเนา", "Print copy")}</Button>
              <Button variant="primary" onClick={() => print(["original"])}>{tx("พิมพ์ต้นฉบับ", "Print original")}</Button>
            </>
          ) : null
        }
      >
        <TaxDocumentPaper doc={viewing} brand={brand} copy="original" />
      </Modal>
      <PrintArea token={printToken} onDone={() => setPrintToken(0)}>
        {viewing ? printCopies.map((copy) => <TaxDocumentPaper key={copy} doc={viewing} brand={brand} copy={copy} />) : null}
      </PrintArea>

      {/* แก้ไข/สร้างฉบับร่าง */}
      <Modal
        open={!!editor}
        size="lg"
        title={editor?.id ? tx("แก้ไขฉบับร่าง", "Edit draft") : tx("สร้างเอกสารภาษี", "New tax document")}
        onClose={() => setEditor(null)}
        footer={
          <>
            <Button onClick={() => setEditor(null)}>{tx("ปิด", "Close")}</Button>
            <Button onClick={saveEditor} disabled={busy}>{tx("บันทึกฉบับร่าง", "Save draft")}</Button>
            {editor?.id ? <Button variant="primary" disabled={busy} onClick={async () => { const saved = await run(() => taxDocumentService.updateDraft(orgId, editor.id, isCreditEdit ? { reason: form.reason, note: form.note } : { buyer: form.buyer, items: form.lines, discount: form.discount, priceMode: form.priceMode, note: form.note })); if (saved) issue(saved); }}>{tx("บันทึกและออกเอกสาร", "Save & issue")}</Button> : null}
          </>
        }
      >
        {!editor?.id ? (
          <>
            <Select label={tx("ประเภทเอกสาร", "Document type")} options={typeOptions} value={form.docType} onChange={(e) => setForm({ ...form, docType: e.target.value })} />
            <Select label={tx("ลูกค้า (ดึงข้อมูลภาษีมาให้)", "Customer (prefill tax info)")} placeholder={tx("— ไม่เลือก กรอกเอง —", "— none, type manually —")} options={(customers || []).map((c) => ({ value: c.id, label: c.name }))} value={form.customerId} onChange={(e) => pickCustomer(e.target.value)} />
          </>
        ) : null}

        {isCreditEdit ? (
          <Textarea label={tx("เหตุผลการลดหนี้", "Reason")} rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
        ) : (
          <>
            <div className="u-strong u-fs-13 u-mb-8">{tx("ผู้ซื้อ", "Buyer")}</div>
            <Input label={tx("ชื่อผู้ซื้อ", "Buyer name")} value={form.buyer.name || ""} onChange={(e) => setForm({ ...form, buyer: { ...form.buyer, name: e.target.value } })} />
            <Textarea label={tx("ที่อยู่ผู้ซื้อ", "Buyer address")} rows={2} value={form.buyer.address || ""} onChange={(e) => setForm({ ...form, buyer: { ...form.buyer, address: e.target.value } })} />
            <BuyerTaxFields value={{ taxId: form.buyer.taxId, branchCode: form.buyer.branchCode, isJuristic: form.buyer.isJuristic, taxName: "", taxAddress: "" }} onChange={setBuyerTax} hideNameAddress />

            <div className="u-strong u-fs-13 u-mb-8 u-mt-16">{tx("รายการ", "Line items")}</div>
            {form.lines.map((line, index) => (
              <div key={line.lineId} style={{ display: "grid", gridTemplateColumns: "1fr 110px 80px 32px", gap: 8, alignItems: "end" }}>
                <Input placeholder={tx("รายการ", "Item")} value={line.name} onChange={(e) => setLine(index, { name: e.target.value })} />
                <Input type="number" min="0" placeholder={tx("ราคา", "Price")} value={line.price} onChange={(e) => setLine(index, { price: e.target.value })} />
                <Input type="number" min="0" placeholder={tx("จำนวน", "Qty")} value={line.quantity} onChange={(e) => setLine(index, { quantity: e.target.value })} />
                <Button size="sm" variant="danger" onClick={() => setForm((prev) => ({ ...prev, lines: prev.lines.filter((_, i) => i !== index) }))}>×</Button>
              </div>
            ))}
            <Button size="sm" className="u-mb-16" onClick={() => setForm((prev) => ({ ...prev, lines: [...prev.lines, newLine()] }))}>+ {tx("เพิ่มรายการ", "Add line")}</Button>

            <div className="grid-2">
              <Input label={tx("ส่วนลดทั้งใบ", "Discount")} type="number" min="0" value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })} />
              <Select
                label={tx("ราคาที่กรอก", "Entered prices")}
                options={[{ value: "exclusive", label: tx("ยังไม่รวม VAT", "Excluding VAT") }, { value: "inclusive", label: tx("รวม VAT แล้ว", "Including VAT") }]}
                value={form.priceMode}
                onChange={(e) => setForm({ ...form, priceMode: e.target.value })}
              />
            </div>
            <div className="doc-total">
              <div className="doc-total__row"><span>{tx("มูลค่าก่อนภาษี", "Before VAT")}</span><span>{baht(calc.base)}</span></div>
              <div className="doc-total__row"><span>VAT 7%</span><span>{baht(calc.vat)}</span></div>
              <div className="doc-total__row doc-total__row--grand"><span>{tx("รวมทั้งสิ้น", "Total")}</span><span>{baht(calc.total)}</span></div>
            </div>
          </>
        )}
        <Input label={tx("หมายเหตุ", "Note")} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
      </Modal>

      {/* ยกเลิก */}
      <Modal
        open={!!cancelTarget}
        size="sm"
        title={tx("ยกเลิกเอกสาร", "Cancel document")}
        onClose={() => setCancelTarget(null)}
        footer={
          <>
            <Button onClick={() => setCancelTarget(null)}>{tx("ไม่ยกเลิก", "Back")}</Button>
            <Button variant="danger" onClick={confirmCancel} disabled={busy}>{tx("ยืนยันยกเลิก", "Confirm cancel")}</Button>
          </>
        }
      >
        <div className="u-fs-13 u-muted u-mb-8">{tx("เอกสารจะไม่ถูกลบ และเลขที่นี้จะไม่ถูกนำกลับมาใช้อีก", "The row stays and its number is never reused.")}</div>
        <Textarea label={tx("เหตุผลที่ยกเลิก (บังคับ)", "Reason (required)")} rows={3} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
      </Modal>

      {/* ใบลดหนี้ */}
      <Modal
        open={!!creditTarget}
        size="sm"
        title={tx(`ออกใบลดหนี้ อ้าง ${creditTarget?.docNo || ""}`, "Create credit note")}
        onClose={() => setCreditTarget(null)}
        footer={
          <>
            <Button onClick={() => setCreditTarget(null)}>{tx("ปิด", "Close")}</Button>
            <Button variant="primary" onClick={confirmCredit} disabled={busy}>{tx("สร้างฉบับร่าง", "Create draft")}</Button>
          </>
        }
      >
        <div className="u-fs-13 u-muted u-mb-8">{tx(`ลดหนี้ได้อีกไม่เกิน ${baht(credit.remaining)} (ยอดรวม VAT)`, `You can credit up to ${baht(credit.remaining)} more`)}</div>
        <Input label={tx("ยอดที่ลดหนี้ (รวม VAT)", "Credit amount (incl. VAT)")} type="number" min="0" value={credit.amount} onChange={(e) => setCredit({ ...credit, amount: e.target.value })} />
        <Textarea label={tx("เหตุผลการลดหนี้ (บังคับ)", "Reason (required)")} rows={2} value={credit.reason} onChange={(e) => setCredit({ ...credit, reason: e.target.value })} />
      </Modal>
    </>
  );
}
